/**
 * The station choices (snippets/dc-station-configurator.liquid). StationPadlockPicker: the padlock
 * as model cards, then that model's colour swatches. StationChoice, at the end: the tag style.
 *
 * Every swatch of every model is a radio under ONE cart property name, so exactly one padlock is
 * posted whichever model is showing. This element only decides which model's swatches are on
 * screen and keeps the choice sensible when the model changes:
 *
 *   - the same colour is kept when the new model has it ("Red" on one padlock stays red on the
 *     next), matched on data-colour-key, the swatch's hex, because a colour's NAME differs
 *     between products on the French store ("Noir" on one padlock, "Le noir" on another);
 *   - otherwise the new model's first colour is chosen, so a hidden model's swatch can never be
 *     the one that is posted.
 *
 * The model radios post nothing (no form=); they are picker state only.
 */
class StationPadlockPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = event.target;
      if (!target || !target.matches) return;
      if (target.matches("[data-station-model]")) this.showModel(target.value);
      else if (target.matches("[data-station-swatches] input[type=radio]")) this.updateReadout(target);
    });

    // A reload can restore a swatch of another model than the one Liquid showed. The posted
    // swatch wins: its model is shown and its card checked, so screen and cart agree.
    const checked = this.checkedSwatch();
    const group = checked?.closest("[data-station-swatches]");
    if (group) {
      const modelId = group.dataset.stationSwatches;
      this.groups().forEach((g) => this.setShown(g, g === group));
      const model = [...this.querySelectorAll("[data-station-model]")].find((r) => r.value === modelId);
      if (model) model.checked = true;
      this.updateReadout(checked);
    }
  }

  groups() {
    return [...this.querySelectorAll("[data-station-swatches]")];
  }

  checkedSwatch() {
    return this.querySelector("[data-station-swatches] input[type=radio]:checked");
  }

  /** Show one model's swatches and move the posted choice into them. */
  showModel(modelId) {
    const previous = this.checkedSwatch();
    const colourKey = previous?.dataset.colourKey || "";
    let target = null;

    for (const group of this.groups()) {
      const shown = group.dataset.stationSwatches === String(modelId);
      this.setShown(group, shown);
      if (!shown) continue;
      const radios = [...group.querySelectorAll("input[type=radio]")];
      target = radios.find((r) => colourKey && r.dataset.colourKey === colourKey) || radios[0] || null;
    }

    if (target && target !== previous) {
      target.checked = true;
      // A real change, so product-configurator.js and the add-to-cart total hear it.
      target.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (target) {
      this.updateReadout(target);
    }
  }

  /**
   * Shown or not, a group keeps its place: the groups share one grid cell sized to the model with
   * the most colours, so changing model moves nothing below it. An unshown group is invisible
   * (dc-station-off) and inert, so its swatches can be neither seen, focused nor clicked.
   */
  setShown(group, shown) {
    group.classList.toggle("dc-station-off", !shown);
    group.inert = !shown;
  }

  /** "Red · LOTO030" beside the chosen model's colour row. */
  updateReadout(radio) {
    const group = radio.closest("[data-station-swatches]");
    const readout = group?.querySelector("[data-station-readout]");
    if (readout) readout.textContent = radio.dataset.readout || "";
  }
}

/**
 * A one-step station choice (the tag style): keeps the readout beside its heading on the chosen
 * card's name and SKU, "Style 1 · LST01", as the padlock's colour row does (Sam, 2026-10-15).
 * Also on connect, so a reload that restored another card shows that card's SKU.
 */
class StationChoice extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", (event) => {
      if (event.target?.dataset?.readout !== undefined) this.updateReadout(event.target);
    });
    const checked = this.querySelector("input[type=radio][data-readout]:checked");
    if (checked) this.updateReadout(checked);
  }

  updateReadout(radio) {
    const readout = this.querySelector("[data-station-readout]");
    if (readout) readout.textContent = radio.dataset.readout || "";
  }
}

customElements.define("station-padlock-picker", StationPadlockPicker);
customElements.define("station-choice", StationChoice);
